<script setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { usePremiumStore } from '@state/stores/premium'
import { centsToDollars } from '../../../../utils/format-price'
import formatDate from '../../../../utils/format-date-full'
import { encodeEmailToAnchor } from '../../../../utils/email'
import { supportEmail } from '../../../../configuration'

defineProps({
    isStripePortalLoading: {
        type: Boolean,
        default: false,
    },
    allowUpdateCard: {
        type: Boolean,
    },
})

defineEmits(['managePremium'])

const premiumStore = usePremiumStore()
const router = useRouter()

const currentSubscription = computed(() => premiumStore.currentSubscription)
const isEntitledForTrial = computed(() => !premiumStore.hasEverBeenPremium)
const paymentMethod = computed(() => premiumStore.paymentMethod)
const hasActivePlan = computed(
    () => currentSubscription.value && new Date(currentSubscription.value.endTime) > new Date()
)
const currentPlan = computed(() => (hasActivePlan.value ? `Premium` : 'FRISKY Free'))
const hasLegacyPlan = computed(() => currentSubscription.value.plan.active === 0)

const subtitle = computed(() => {
    if (!hasActivePlan.value) return ''

    if (paymentMethod.value === 'apple') {
        return 'Your subscription is managed through Apple. To manage your subscription, go to iOS Settings > Apple ID > Subscriptions'
    }

    if (paymentMethod.value === 'google') {
        return 'Your subscription is managed through Google Play. To manage your subscription, go to Google Play Store > Account > Subscriptions'
    }

    if (paymentMethod.value === 'manual') {
        return `Enjoy free Premium subscription on us! It will expire on ${formatDate(new Date(currentSubscription.value.endTime))}`
    }

    if (currentSubscription.value?.cancelTime) {
        return `Your subscription is canceled. It will expire on ${formatDate(new Date(currentSubscription.value.endTime))}`
    }

    if (hasLegacyPlan.value) {
        return `Your next bill is for ${ centsToDollars(currentSubscription.value.plan.price) } on ${ formatDate(new Date(currentSubscription.value.endTime)) } <br> Need to cancel? Contact ${encodeEmailToAnchor(supportEmail, 'Support')}.`
    }

    return `Your next bill is for ${ centsToDollars(currentSubscription.value.plan.price) } on ${ formatDate(new Date(currentSubscription.value.endTime)) }`
})

const goToEditProfile = () => {
    router.push('/account/edit-profile')
}

const explorePlans = () => {
    router.push('/premium/view-plans')
}
</script>

<template>
    <div class="subscription-header">
        <BaseCard class="plan-card">
            <h3 class="plan-name text-faded">{{ currentPlan }}</h3>
            <!-- <p class="plan-subtitle text-faded">{{ subtitle }}</p> -->
            <p
                v-safe-html="subtitle"
                class="plan-subtitle text-faded"
            />
            <div
                v-if="isEntitledForTrial"
                class="plan-actions"
            >
                <BaseButton
                    class="explore-plans-button"
                    variant="secondary"
                    size="l"
                    @click="explorePlans"
                >Explore Plans</BaseButton>
            </div>
        </BaseCard>
        <div class="side-cards">
            <div
                v-if="!hasActivePlan"
                class="side-card"
                @click="explorePlans"
            >
                <BaseCard
                    class="side-card-content fw-bold"
                    theme="gradient-vibrand-highlight"
                >
                    {{ isEntitledForTrial ? 'Start free trial' : 'Upgrade to Premium' }}
                </BaseCard>
            </div>
            <div
                v-else
                class="side-card"
                @click="goToEditProfile"
            >
                <BaseCard class="side-card-content">
                    <BaseIcon
                        class="side-card-icon"
                        name="user"
                    />
                    Edit Profile
                </BaseCard>
            </div>
            <div
                v-if="allowUpdateCard"
                class="side-card"
                @click="$emit('managePremium')"
            >
                <BaseCard :class="['side-card-content', { 'skeleton': isStripePortalLoading }]">
                    <BaseIcon
                        class="side-card-icon"
                        name="wallet"
                    />
                    Update card
                </BaseCard>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
@import '@design';

.subscription-header {
    display: flex;
    flex-flow: row;
    column-gap: $s-m;
    align-items: stretch;
    justify-content: space-around;
}

.plan-card {
    flex: 2 1 0%;
    min-height: 170px;
}

.plan-header {
    @extend %contentTitleM;
}

.plan-name {
    @extend %h4;

    margin-top: $s-xs;
    margin-bottom: 0;
}

.plan-subtitle {
    margin-top: $s-xxs;
}

.plan-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-top: $s-xl;
}

.side-cards {
    display: flex;
    flex: 1 1 0%;
    flex-flow: column;
    row-gap: $s-m;
    column-gap: 0;
}

.side-card {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    overflow: hidden;
    cursor: pointer;

    &:hover {
        .side-card-content {
            // state-hover composited over the surface — the rest state is also surface-overlay
            background: linear-gradient($c-state-hover, $c-state-hover), $c-surface-overlay;
        }
    }
}

.card-link {
    display: block;
}

.side-card-content {
    @extend %contentSubtitleM;

    display: flex;
    flex-direction: column;
    row-gap: $s-m;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    overflow: hidden;
    text-align: center;
}

.side-card-icon {
    max-width: 23px;
    max-height: 23px;
}

@include tablet {
    .plan-card {
        flex: 2.5 1 0%;
    }
}
</style>
